import { useApi, useSavings } from '@/api';
import {
  ActivityCard,
  EntityName,
  LinkBanner,
  KpiCard,
  Link,
  Page,
  PageSkeleton
} from '@/components';
import { useSearchParams } from '@/hooks';
import { tokensMapping } from '@/utils/tokens';
import {
  CryptoIcon,
  EtherScanPictogram,
  Flex,
  Heading,
  LinkWrapper,
  Box,
  Text
} from '@blockanalitica/ui';
import HistoricTabs from './HistoricTabs';
import HoldersDonutChart from './HoldersDonutChart';
import TopDepositors from './TopDepositors';

export default function SSRPage() {
  const [daysAgo] = useSearchParams('days_ago', '1');
  const [data, error] = useApi({
    url: `/save/ssr/`,
    queryParams: {
      days_ago: daysAgo
    }
  });

  const [savings, savingsError] = useSavings(daysAgo);

  if (error) {
    throw error;
  }
  if (savingsError) {
    throw savingsError;
  }

  if (data && savings) {
    return (
      <Page
        headerFlexDirection={['column', 'row']}
        title={
          <EntityName
            symbol1={
              <CryptoIcon
                name={'USDS'}
                address={tokensMapping('USDS').address}
                size="4"
              />
            }>
            <Heading size="1">
              <Flex gap="xs">
                Sky Savings Rate
                <a
                  href={`https://etherscan.io/address/0xa3931d71877C0E7a3148CB7Eb4463524FEc27fbD`}
                  target="_blank"
                  rel="noreferrer">
                  <EtherScanPictogram size="0" />
                </a>
              </Flex>
            </Heading>
          </EntityName>
        }>
        <LinkBanner
          text="Supply to SSR"
          linkTo="https://app.sky.money/?widget=savings">
          <Box paddingTop="xs" paddingBottom="m" maxWidth="85%">
            <Heading size="2">About the Sky Savings Rate</Heading>
            <br />
            <Text>
              When you supply USDS to the Sky Savings Rate module of the
              decentralized Sky Protocol, you access the Sky Savings Rate and
              may accumulate additional USDS over time. No minimum supply amount
              is required, and you always maintain control of your supplied
              assets, as this feature is non-custodial.
            </Text>
          </Box>
        </LinkBanner>
        <Flex gap={['m', 's']} flexDirection={['column', 'row']}>
          <KpiCard
            title="Total Supply"
            variant="usds"
            field="total_susds"
            data={savings}
            options={{
              currency: tokensMapping('USDS'),
              notation: 'standard',
              valueOptions: { currencyValueOptions: { iconSize: '3' } }
            }}
          />
          <KpiCard
            title="Savings rate"
            data={data}
            field="rate"
            options={{
              percentage: true,
              variants: { valuePercentageValue: 'dynamic' }
            }}
          />
        </Flex>

        <HistoricTabs />

        <ActivityCard url={'/save/ssr/activity/'} linkTo={'activity'} />

        <Flex gap={['m', 's']} flexDirection={['column', 'row']}>
          <Flex gap={['m', 's']} flexDirection="column" width="100%">
            <LinkWrapper
              to="suppliers"
              // @ts-ignore
              as={Link}
              variant="kpiCardLinkWithBlendMode">
              <KpiCard
                title="N° of Suppliers"
                data={data}
                field="depositors"
                options={{
                  linkArrow: true,
                  variants: { valuePercentageValue: 'dynamic' }
                }}
              />
            </LinkWrapper>
            <TopDepositors flexBasis="100%" />
          </Flex>
          <Flex flexBasis="100%" flexDirection="column" alignItems="center">
            <Heading size="3">Suppliers Distribution</Heading>
            <HoldersDonutChart />
          </Flex>
        </Flex>
      </Page>
    );
  }
  return <PageSkeleton />;
}
